/**
 * What the map's forecast mode shows (#327 MR5d): the pure half of
 * `hooks/useForecastMapSelection`, lifted out of the webapp by step 4 S4-1 so
 * the mobile forecast mode resolves, notices and writes back exactly as /map
 * does. Nothing here reads a map, a URL or a store: the hook hands the app's
 * state in.
 */
import type {ForecastMapConfig} from '../../api/types';
import {
  matchesForecastConfigRef,
  forecastConfigRef,
} from '../ForecastMap/configRef';
import {isInteractiveForecastConfig} from '../ForecastMap/interactiveVariables';
import {viewCoverage} from './coverage';
import {
  AUTO_KEEP_THRESHOLD,
  FORECAST_VARIABLES,
  type ForecastCatalogModel,
  type ForecastModelKey,
  type ForecastVariableKey,
  forecastModelTitle,
  forecastVariableKey,
} from './forecastCatalog';
import type {MapBounds} from './mapModes';

/**
 * What forecast mode shows:
 * - `auto`: the model Auto picks for the view, on a rail variable;
 * - `model`: a model the user hand-picked (a config: `config` in the URL);
 * - `pinned`: a config the rail has no place for, opened by URL -- the
 *   user's own builder config, a variable the rail lacks (gusts), a second
 *   config of a model's variable. Picking Auto returns to the rail.
 */
export type ForecastMapSelection =
  | {kind: 'auto'; variable: ForecastVariableKey}
  | {
      kind: 'model';
      variable: ForecastVariableKey;
      modelKey: ForecastModelKey;
      config: ForecastMapConfig;
    }
  | {kind: 'pinned'; config: ForecastMapConfig};

/** A notice the mode raised (the hand-picked "left its area" is derived). */
export type ForecastNoticeState =
  | {
      id: number;
      kind: 'autoSwitched';
      model: string;
      previous: string;
      coverage: number;
    }
  | {
      id: number;
      kind: 'variableOnly';
      variable: ForecastVariableKey;
      model: string;
    };

/**
 * Why the URL's config is not shown (the Banner): it does not exist (or
 * cannot be drawn here), or it is a Premium model's (#327 MR7a).
 */
export type ForecastUnavailable =
  | {kind: 'missing'; name: string}
  | {kind: 'locked'; model: string; fallback: string};

/**
 * Something this account cannot open was chosen (#327 MR7a): a locked
 * model in the list or a locked variable on the rail. The mode offers
 * Premium instead; nothing switches.
 */
export type ForecastLockedPick =
  | {kind: 'model'; model: string}
  | {kind: 'variable'; variable: ForecastVariableKey};

/** The hand-picked model covers too little of the view (never dismissed). */
export type ForecastLeftArea = {
  model: string;
  coverage: number;
  /** The model Auto would draw instead ("Switch to …"). */
  autoModel: string;
};

/**
 * The view Auto works on, and how many times the map settled: `seq` counts
 * every settle (Auto re-picks on each), `userSeq` only those the user's own
 * gesture caused -- not a camera change the app makes (a fit). `bounds` is
 * null until the map can say.
 */
export type ForecastMapSelectionView = {
  bounds: MapBounds | null;
  seq: number;
  userSeq: number;
};

/**
 * The selection as the URL names it: Auto's `variable`, or the config's ref
 * (its uuid; a legacy link's id is read too). One of the two at most.
 */
export type ForecastMapSelectionUrl = {
  config: string | null;
  variable: ForecastVariableKey | null;
};

/** How long an Auto notice stays without an interaction. */
export const FORECAST_NOTICE_TIMEOUT_MS = 6000;

/** The view Auto works on before the map can say. */
export const FORECAST_MAP_SELECTION_WORLD: MapBounds = [-180, -85, 180, 85];

/** "GFS · Temperature 2 m": a config named with its model. */
export const forecastConfigLabel = (config: ForecastMapConfig) =>
  `${forecastModelTitle(config.forecast_model)} · ${config.name}`;

/**
 * What the map can draw, split into what this account may open
 * (`available`) and the locked presets, which are only ever offered.
 */
export const splitForecastCatalog = (
  configs: readonly ForecastMapConfig[],
): {available: ForecastMapConfig[]; lockedConfigs: ForecastMapConfig[]} => {
  const interactive = configs.filter((config) =>
    isInteractiveForecastConfig(config, configs),
  );
  return {
    available: interactive.filter((config) => config.locked !== true),
    lockedConfigs: interactive.filter((config) => config.locked === true),
  };
};

/** The model and rail variable a config is the model's config for. */
export const forecastConfigOwner = (
  models: readonly ForecastCatalogModel[],
  configId: number,
): {model: ForecastCatalogModel; variable: ForecastVariableKey} | null => {
  for (const model of models) {
    for (const variable of FORECAST_VARIABLES) {
      if (model.configs[variable]?.id === configId) {
        return {model, variable};
      }
    }
  }
  return null;
};

/** The model's config for the variable, unless it has none or it is locked. */
export const usableForecastConfig = (
  model: ForecastCatalogModel,
  variable: ForecastVariableKey,
): ForecastMapConfig | null => {
  const config = model.configs[variable];
  return config !== undefined && config.locked !== true ? config : null;
};

export type ForecastMapSelectionResolution = {
  /** Null when nothing can be drawn. */
  selection: ForecastMapSelection | null;
  /** The URL names a config that does not exist or cannot be drawn. */
  invalidUrlConfig: boolean;
  /** The URL names a Premium model's config: Auto instead. */
  lockedUrlConfig: ForecastMapConfig | null;
  /** The saved config is gone (or locked): forget it, silently. */
  clearSaved: boolean;
};

/**
 * Which forecast to show, from the URL first (it is the state; the user's
 * choices write it):
 * 1. a valid URL `config` (exactly `isInteractiveForecastConfig`): a
 *    hand-picked model, or pinned when the rail has no place for it -- a
 *    locked one opens Auto instead, on its variable when this account has
 *    it;
 * 2. a URL `variable`: Auto on it;
 * 3. the config saved on this device (saved only when hand-picked);
 * 4. Auto on the saved variable.
 * Auto on a variable the rail lacks is Auto on the rail's first. A catalog
 * with nothing for the rail (only own configs) pins the first config the map
 * can draw. Call it with a LOADED catalog.
 */
export const resolveForecastMapSelection = ({
  available,
  lockedConfigs,
  models,
  railVariables,
  url,
  savedConfigId,
  savedVariable,
}: {
  available: readonly ForecastMapConfig[];
  lockedConfigs: readonly ForecastMapConfig[];
  models: readonly ForecastCatalogModel[];
  railVariables: readonly ForecastVariableKey[];
  url: ForecastMapSelectionUrl;
  savedConfigId: number | null;
  savedVariable: ForecastVariableKey;
}): ForecastMapSelectionResolution => {
  const byId = (id: number | null) =>
    id === null ? null : (available.find((config) => config.id === id) ?? null);
  const fromConfig = (config: ForecastMapConfig): ForecastMapSelection => {
    const owner = forecastConfigOwner(models, config.id);
    return owner === null
      ? {kind: 'pinned', config}
      : {
          kind: 'model',
          variable: owner.variable,
          modelKey: owner.model.key,
          config,
        };
  };
  const autoOn = (
    variable: ForecastVariableKey,
  ): ForecastMapSelection | null =>
    railVariables.length === 0
      ? null
      : {
          kind: 'auto',
          variable: railVariables.includes(variable)
            ? variable
            : railVariables[0],
        };

  // The URL names a config by its uuid, or by its id on a legacy link.
  const byRef = (configs: readonly ForecastMapConfig[]) =>
    configs.find((config) => matchesForecastConfigRef(config, url.config)) ??
    null;
  const urlConfig = byRef(available);
  // A Premium model's config: Auto instead, on its variable when this
  // account has it, with the locked Banner (never "isn't available").
  const lockedUrlConfig = urlConfig === null ? byRef(lockedConfigs) : null;
  const invalidUrlConfig =
    url.config !== null && urlConfig === null && lockedUrlConfig === null;
  const savedConfig = byId(savedConfigId);
  const clearSaved = savedConfigId !== null && savedConfig === null;
  let selection: ForecastMapSelection | null;
  if (urlConfig !== null) {
    selection = fromConfig(urlConfig);
  } else if (lockedUrlConfig !== null) {
    const lockedVariable = forecastVariableKey(lockedUrlConfig);
    selection = autoOn(
      lockedVariable !== null && railVariables.includes(lockedVariable)
        ? lockedVariable
        : (url.variable ?? savedVariable),
    );
  } else if (url.variable !== null) {
    selection = autoOn(url.variable);
  } else if (savedConfig !== null) {
    selection = fromConfig(savedConfig);
  } else {
    selection = autoOn(savedVariable);
  }
  // A catalog with nothing for the rail (only own configs): the first
  // config the map can draw, pinned.
  if (selection === null && available.length > 0) {
    selection = {kind: 'pinned', config: available[0]};
  }
  return {selection, invalidUrlConfig, lockedUrlConfig, clearSaved};
};

/**
 * The variable Auto and the model list work on: the selection's, or for a
 * pinned map (or none), the one Auto would come back to.
 */
export const forecastMapSelectionVariable = ({
  selection,
  railVariables,
  savedVariable,
}: {
  selection: ForecastMapSelection | null;
  railVariables: readonly ForecastVariableKey[];
  savedVariable: ForecastVariableKey;
}): ForecastVariableKey =>
  selection === null || selection.kind === 'pinned'
    ? railVariables.includes(savedVariable)
      ? savedVariable
      : (railVariables[0] ?? 'temperature')
    : selection.variable;

/**
 * Auto's model when no model covers the view: the first this account may
 * open the variable on (the camera then fits it).
 */
export const fallbackAutoModel = (
  models: readonly ForecastCatalogModel[],
  variable: ForecastVariableKey,
): ForecastCatalogModel | null =>
  models.find((model) => usableForecastConfig(model, variable) !== null) ??
  null;

/** The config drawn: Auto's model's for the variable, else the selection's. */
export const drawnForecastConfig = (
  selection: ForecastMapSelection | null,
  autoModel: ForecastCatalogModel | null,
): ForecastMapConfig | null => {
  if (selection === null) {
    return null;
  }
  if (selection.kind === 'auto') {
    return autoModel === null
      ? null
      : usableForecastConfig(autoModel, selection.variable);
  }
  return selection.config;
};

/** Auto's model as last picked, and what it was picked for. */
export type ForecastAutoPick = {
  key: ForecastModelKey | null;
  variable: ForecastVariableKey;
  /** The view's `seq` it was picked on. */
  seq: number;
  /** Whether Auto was what the map showed. */
  isAuto: boolean;
};

/**
 * The notice for a switch the VIEW caused (same variable, Auto before and
 * after, a new settle, another model): said only when the model drawn before
 * covers less than `AUTO_KEEP_THRESHOLD` of the view now. A move to a finer
 * model has nothing to explain ("ICON-EU covers 100% of this view").
 */
export const autoSwitchedNotice = ({
  previous,
  next,
  models,
  bounds,
}: {
  previous: ForecastAutoPick;
  next: ForecastAutoPick;
  models: readonly ForecastCatalogModel[];
  bounds: MapBounds;
}): Omit<Extract<ForecastNoticeState, {kind: 'autoSwitched'}>, 'id'> | null => {
  const modelOf = (key: ForecastModelKey | null) =>
    key === null ? null : (models.find((model) => model.key === key) ?? null);
  const before = modelOf(previous.key);
  const after = modelOf(next.key);
  if (
    !previous.isAuto ||
    !next.isAuto ||
    previous.variable !== next.variable ||
    previous.seq === next.seq ||
    before === null ||
    after === null ||
    next.key === previous.key ||
    before.domain === undefined
  ) {
    return null;
  }
  const coverage = viewCoverage(bounds, before.domain);
  return coverage < AUTO_KEEP_THRESHOLD
    ? {
        kind: 'autoSwitched',
        model: after.title,
        previous: before.title,
        coverage,
      }
    : null;
};

/**
 * The selection's identity: a new one resets "the user moved since the
 * pick". Auto is keyed on its variable, the rest on the config.
 */
export const forecastMapSelectionKey = (
  selection: ForecastMapSelection | null,
): string | null =>
  selection === null
    ? null
    : selection.kind === 'auto'
      ? `auto:${selection.variable}`
      : `config:${selection.config.id}`;

/**
 * A hand-picked model covering less than `AUTO_KEEP_THRESHOLD` of a view the
 * USER moved to after picking it: opening on a saved model over a wide view
 * says nothing until the map moves, and neither does the mode's own fit to
 * its domain.
 */
export const forecastLeftArea = ({
  selectedModel,
  autoModel,
  bounds,
  movedSincePick,
}: {
  selectedModel: ForecastCatalogModel | null;
  autoModel: ForecastCatalogModel | null;
  bounds: MapBounds;
  movedSincePick: boolean;
}): ForecastLeftArea | null => {
  if (
    selectedModel === null ||
    selectedModel.domain === undefined ||
    !movedSincePick ||
    autoModel === null ||
    autoModel.key === selectedModel.key
  ) {
    return null;
  }
  const coverage = viewCoverage(bounds, selectedModel.domain);
  return coverage < AUTO_KEEP_THRESHOLD
    ? {model: selectedModel.title, coverage, autoModel: autoModel.title}
    : null;
};

/**
 * The Banner for an invalid or locked URL config, once something is drawn
 * in its place (it names what is shown instead).
 */
export const forecastUnavailableBanner = ({
  config,
  invalidUrlConfig,
  lockedUrlConfig,
}: {
  config: ForecastMapConfig | null;
  invalidUrlConfig: boolean;
  lockedUrlConfig: ForecastMapConfig | null;
}): ForecastUnavailable | null => {
  if (config === null) {
    return null;
  }
  if (invalidUrlConfig) {
    return {kind: 'missing', name: forecastConfigLabel(config)};
  }
  if (lockedUrlConfig !== null) {
    return {
      kind: 'locked',
      model: forecastModelTitle(lockedUrlConfig.forecast_model),
      fallback: forecastModelTitle(config.forecast_model),
    };
  }
  return null;
};

/** The URL that names the selection: Auto's variable, else the config's uuid. */
export const forecastMapSelectionUrl = (
  selection: ForecastMapSelection | null,
): ForecastMapSelectionUrl => ({
  config:
    selection === null || selection.kind === 'auto'
      ? null
      : forecastConfigRef(selection.config),
  variable: selection?.kind === 'auto' ? selection.variable : null,
});

/**
 * "Cloud cover comes from ECMWF HRES only": when one model alone has the
 * variable, and there is a choice of models at all (a GFS-only catalog has
 * nothing to say, locked models listed or not -- this account cannot choose
 * them).
 */
export const variableOnlyNotice = (
  models: readonly ForecastCatalogModel[],
  variable: ForecastVariableKey,
): {variable: ForecastVariableKey; model: string} | null => {
  const owners = models.filter(
    (model) => usableForecastConfig(model, variable) !== null,
  );
  const choosable = models.filter((model) =>
    FORECAST_VARIABLES.some((key) => usableForecastConfig(model, key) !== null),
  );
  return owners.length === 1 && choosable.length > 1
    ? {variable, model: owners[0].title}
    : null;
};
